import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu, Tooltip as MantineTooltip } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate } from '../../../../helpers/date-time-helpers';
import { Metric, WorkPaceChartType } from '../../../work-periods/work-periods.type';
import { useIsExporting } from '../artifact-export-context';
import type { ArtifactComponentProps } from '../artifact-registry';
import {
  ChartTooltip,
  buildScopedWorkPaceQuickLinks,
  buildWorkPaceArtifactChartData,
  getInitialChartType,
  getInitialMetric,
  getInitialWorkPeriodIndex,
  getMetricLabel,
  isWorkPacePayload,
  openArtifactQuickLink,
  renderStatusDot,
} from './work-pace-artifact.helpers';
import type { PaceLineKey, WorkPaceToggleButtonProps } from './work-pace-artifact.type';

/**
 * Renders the Flora chat artifact view for backend-provided Work Pace data.
 *
 * @param props - Standard Flora artifact component props. `initialState` (when
 *   provided by the export route) seeds `selectedIndex`, `metric`, and
 *   `chartType` so screenshots are reproducible — see `ArtifactComponentProps`
 *   JSDoc for the recognized keys.
 * @returns Work Pace artifact chart and controls.
 */
export function WorkPaceArtifact({
  data,
  config,
  quickLinks,
  onQuickLinksChange,
  initialState,
}: ArtifactComponentProps) {
  const { t } = useTranslation();
  const payload = useMemo(() => {
    if (isWorkPacePayload(data)) return data;
    return null;
  }, [data]);

  const isExporting = useIsExporting();
  const workPeriods = payload?.work_periods ?? [];
  const [selectedIndex, setSelectedIndex] = useState(() => getInitialWorkPeriodIndex(initialState));
  const [metric, setMetric] = useState<Metric>(() => getInitialMetric(initialState));
  const [chartType, setChartType] = useState<WorkPaceChartType>(() => getInitialChartType(initialState));

  useEffect(() => {
    setSelectedIndex((index) => Math.min(index, Math.max(workPeriods.length - 1, 0)));
  }, [workPeriods.length]);

  const currentWP = workPeriods[selectedIndex];

  useEffect(() => {
    if (!onQuickLinksChange) return;
    if (!currentWP) {
      onQuickLinksChange([]);
      return;
    }

    onQuickLinksChange(buildScopedWorkPaceQuickLinks(currentWP, config));
  }, [onQuickLinksChange, currentWP, config]);

  const chartData = useMemo(() => {
    if (!currentWP?.chart_data) return [];
    return buildWorkPaceArtifactChartData(currentWP.chart_data, metric);
  }, [currentWP, metric]);

  const goPrev = useCallback(() => {
    setSelectedIndex((index) => Math.max(0, index - 1));
  }, []);

  const goNext = useCallback(() => {
    setSelectedIndex((index) => Math.min(workPeriods.length - 1, index + 1));
  }, [workPeriods.length]);

  const handleQuickLinkClick = useCallback((url: string) => {
    openArtifactQuickLink(url);
  }, []);

  if (!payload || workPeriods.length === 0) {
    return <EmptyState>No work pace data available</EmptyState>;
  }

  if (!currentWP) {
    return <EmptyState>Invalid work period selection</EmptyState>;
  }

  const hasManyWPs = workPeriods.length > 1;
  const primaryDataKey: PaceLineKey = chartType === WorkPaceChartType.BurnDown ? 'remaining' : 'complete';
  const primaryName = chartType === WorkPaceChartType.BurnDown ? 'Remaining' : 'Actual';
  const primaryStroke = chartType === WorkPaceChartType.BurnDown ? '#FA5252' : '#4C6EF5';
  const idealName = chartType === WorkPaceChartType.BurnDown ? 'Ideal Remaining' : 'Ideal';
  const idealStroke = chartType === WorkPaceChartType.BurnDown ? '#4C6EF5' : '#40C057';
  const metricLabel = getMetricLabel(metric);
  const goalAttainmentLabel = t('work_periods.burn_up');
  const workCompletionLabel = t('work_periods.burn_down');
  const yAxisLabel =
    chartType === WorkPaceChartType.BurnDown
      ? metric === Metric.Tasks
        ? `${t('common.work_units')} Remaining`
        : `${t('common.estimation')} Remaining`
      : metric === Metric.Tasks
        ? `${t('common.work_units')} Completed`
        : `${t('common.estimation')} Completed`;

  return (
    <ChartContainer>
      <HeaderRow>
        <HeaderLeft>
          {hasManyWPs && (
            <NavBtn type="button" onClick={goPrev} disabled={selectedIndex === 0} aria-label="Previous work period">
              <Icon icon="material-symbols:chevron-left" width={16} height={16} />
            </NavBtn>
          )}

          {hasManyWPs ? (
            <Menu shadow="sm" radius="md" position="bottom-start" offset={4} withinPortal={false}>
              <Menu.Target>
                <WPDropdownBtn type="button" aria-label="Select work period">
                  <WPName>{currentWP.name || `Work Period ${selectedIndex + 1}`}</WPName>
                  <Icon icon="material-symbols:expand-more" width={14} height={14} />
                </WPDropdownBtn>
              </Menu.Target>
              <Menu.Dropdown>
                {workPeriods.map((wp, index) => (
                  <Menu.Item
                    key={wp.id}
                    onClick={() => setSelectedIndex(index)}
                    rightSection={
                      index === selectedIndex ? (
                        <Icon icon="material-symbols:check" width={14} height={14} />
                      ) : undefined
                    }
                    style={{ fontSize: '13px' }}
                  >
                    {wp.name || `Work Period ${index + 1}`}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ) : (
            <WPName>{currentWP.name || 'Work Period'}</WPName>
          )}

          {hasManyWPs && (
            <NavBtn
              type="button"
              onClick={goNext}
              disabled={selectedIndex === workPeriods.length - 1}
              aria-label="Next work period"
            >
              <Icon icon="material-symbols:chevron-right" width={16} height={16} />
            </NavBtn>
          )}

          {currentWP.start_date && currentWP.end_date && (
            <DateRange>
              {currentWP.start_date} - {currentWP.end_date}
            </DateRange>
          )}
        </HeaderLeft>

        <ControlGroup>
          {quickLinks?.map((link) => (
            <MantineTooltip key={link.url} label={link.label} position="top" withArrow>
              <QuickLinkButton type="button" onClick={() => handleQuickLinkClick(link.url)} aria-label={link.label}>
                <Icon icon="material-symbols:open-in-new" width={14} height={14} />
              </QuickLinkButton>
            </MantineTooltip>
          ))}

          <ChartTypeToggle>
            <MantineTooltip label={goalAttainmentLabel} position="top" withArrow>
              <ToggleBtn
                type="button"
                $active={chartType === WorkPaceChartType.BurnUp}
                onClick={() => setChartType(WorkPaceChartType.BurnUp)}
                aria-label={goalAttainmentLabel}
                aria-pressed={chartType === WorkPaceChartType.BurnUp}
              >
                <Icon icon="material-symbols:trending-up" width={16} height={16} />
              </ToggleBtn>
            </MantineTooltip>
            <MantineTooltip label={workCompletionLabel} position="top" withArrow>
              <ToggleBtn
                type="button"
                $active={chartType === WorkPaceChartType.BurnDown}
                onClick={() => setChartType(WorkPaceChartType.BurnDown)}
                aria-label={workCompletionLabel}
                aria-pressed={chartType === WorkPaceChartType.BurnDown}
              >
                <Icon icon="material-symbols:trending-down" width={16} height={16} />
              </ToggleBtn>
            </MantineTooltip>
          </ChartTypeToggle>

          <Menu shadow="sm" radius="md" position="bottom-end" offset={4} withinPortal={false}>
            <Menu.Target>
              <MetricBtn type="button" aria-label="Select metric">
                {metric === Metric.Tasks ? 'Tasks' : 'Points'}
                <Icon icon="material-symbols:expand-more" width={14} height={14} />
              </MetricBtn>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => setMetric(Metric.Tasks)}
                rightSection={
                  metric === Metric.Tasks ? <Icon icon="material-symbols:check" width={14} height={14} /> : undefined
                }
                style={{ fontSize: '13px' }}
              >
                Tasks
              </Menu.Item>
              <Menu.Item
                onClick={() => setMetric(Metric.Points)}
                rightSection={
                  metric === Metric.Points ? <Icon icon="material-symbols:check" width={14} height={14} /> : undefined
                }
                style={{ fontSize: '13px' }}
              >
                Points
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </ControlGroup>
      </HeaderRow>

      {chartData.length === 0 ? (
        <EmptyState>No work pace chart data available</EmptyState>
      ) : (
        <ChartFrame>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 18, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(String(value), { format: 'MMM D' })}
                tick={{ fontSize: 11 }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: 'var(--ink-lighter)' },
                  offset: 10,
                }}
              />
              <RechartsTooltip
                content={(props) => (
                  <ChartTooltip
                    {...props}
                    chartType={chartType}
                    metricLabel={metricLabel}
                    primaryDataKey={primaryDataKey}
                  />
                )}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                key={`${chartType}-${metric}-primary`}
                type="monotone"
                dataKey={primaryDataKey}
                name={primaryName}
                stroke={primaryStroke}
                strokeWidth={2}
                dot={renderStatusDot(primaryDataKey, primaryStroke)}
                activeDot={renderStatusDot(primaryDataKey, primaryStroke, 7)}
                connectNulls={false}
                isAnimationActive={!isExporting}
              />
              <Line
                key={`${chartType}-${metric}-ideal`}
                type="monotone"
                dataKey="ideal"
                name={idealName}
                stroke={idealStroke}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                connectNulls={false}
                isAnimationActive={!isExporting}
              />
              {chartType === WorkPaceChartType.BurnUp && (
                <Line
                  key={`${chartType}-${metric}-total`}
                  type="monotone"
                  dataKey="total"
                  name="Total"
                  stroke="#312e81"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={!isExporting}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      )}
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  width: 100%;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--ink-lighter);
  font-size: 13px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 4px 10px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--ink-base);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--sky-lightest);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const WPDropdownBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 180px;
  height: 26px;
  padding: 0 8px;
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  background: var(--base-white);
  color: var(--ink-dark);
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: var(--sky-lightest);
  }
`;

const WPName = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: var(--ink-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DateRange = styled.span`
  margin-left: 6px;
  color: var(--ink-lighter);
  font-size: 12px;
  white-space: nowrap;
`;

const ChartTypeToggle = styled.div`
  display: inline-flex;
  padding: 2px;
  border: 1px solid var(--sky-light);
  border-radius: 5px;
  background: var(--sky-lightest);
`;

const ToggleBtn = styled.button<WorkPaceToggleButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: ${(props) => (props.$active ? 'var(--secondary-base)' : 'transparent')};
  color: ${(props) => (props.$active ? 'var(--base-white)' : 'var(--ink-base)')};
  cursor: pointer;

  &:hover {
    background: ${(props) => (props.$active ? 'var(--secondary-base)' : 'var(--sky-light)')};
  }
`;

const MetricBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  background: var(--base-white);
  color: var(--ink-dark);
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: var(--sky-lightest);
  }
`;

const QuickLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  background: var(--base-white);
  color: var(--ink-base);
  cursor: pointer;

  &:hover {
    background: var(--sky-lightest);
    color: var(--secondary-base);
  }
`;

const ChartFrame = styled.div`
  width: 100%;
  height: 280px;
`;
